import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { User, AuthResponse, AdminDashboard, UsersResponse } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private subscriptionData = new BehaviorSubject<any>(null);
  subscriptionData$ = this.subscriptionData.asObservable();

  constructor(private http: HttpClient) {
    this.loadStoredUser();
  }

  private loadStoredUser(): void {
    const stored = localStorage.getItem('user');
    if (stored) {
      this.currentUserSubject.next(JSON.parse(stored));
    }
  }

private getHeaders(): HttpHeaders {
  const token = localStorage.getItem('session_token');

  return new HttpHeaders({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  });
}

private getBeneficiaryHeaders(): HttpHeaders {
  const token = localStorage.getItem('beneficiary_token');

  return new HttpHeaders({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  });
}

  // Auth endpoints
  signup(email: string, password: string, name?: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/signup`, { email, password, name })
      .pipe(tap(res => this.handleAuthResponse(res)));
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/login`, { email, password })
      .pipe(tap(res => this.handleAuthResponse(res)));
  }

  logout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/logout`, {}, { headers: this.getHeaders() })
      .pipe(tap(() => this.clearAuth()));
  }
//   logoutBeneficiary(): Observable<any> {
//   return this.http.post(
//     `${this.apiUrl}/auth/logout`,
//     {},
//     { headers: this.getBeneficiaryHeaders()}
//   ).pipe(
//     tap(() => this.clearBeneficiaryAuth())
//   );
// }
logoutBeneficiary(): Observable<any> {
  let token = this.getBeneficiaryHeaders();
 
  return this.http.post(
    `${this.apiUrl}/auth/logout`,
    {},
    {
      headers: token,
      withCredentials: true
    }
  ).pipe(
    tap(() => this.clearBeneficiaryAuth())
  );
}
  

  

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/auth/me`, { headers: this.getHeaders() });
  }

  exchangeSession(sessionId: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/session`, { session_id: sessionId })
      .pipe(tap(res => this.handleAuthResponse(res)));
  }

  // Alias for Google OAuth session exchange
  exchangeGoogleSession(sessionId: string): Observable<AuthResponse> {
    return this.exchangeSession(sessionId);
  }

  // Profile endpoints
  getProfile(): Observable<User> {
    return this.http.get<User>(
      `${this.apiUrl}/user/profile`,
      { headers: this.getHeaders() }
    ).pipe(
      tap(user => {
        localStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
      })
    );
  }

  updateProfile(data: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/user/profile`, data, { headers: this.getHeaders() })
      .pipe(tap(user => {
        localStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
      }));
  }

  deleteAccount(): Observable<any> {
    return this.http.delete(`${this.apiUrl}/user/account`, { headers: this.getHeaders() })
      .pipe(tap(() => this.clearAuth()));
  }

  // Password endpoints
  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/password-reset/request`, { email });
  }

  confirmPasswordReset(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/password-reset/confirm`, { token, new_password: newPassword });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, { headers: this.getHeaders() });
  }

  // Contact
  submitContact(data: { name: string; email: string; phone?: string; subject?: string; message: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/contact`, data);
  }

  // Payments
  createCheckout(originUrl: string): Observable<{ checkout_url: string }> {
    return this.http.post<{ checkout_url: string }>(`${this.apiUrl}/payments/create-checkout`, { origin_url: originUrl }, { headers: this.getHeaders() });
  }

  // Verification
  resendVerification(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/auth/resend-verification`, { email }, { headers: this.getHeaders() });
  }
  verifyEmail(token: string): Observable<any> {
  return this.http.get(
    `${this.apiUrl}/auth/verify-email?token=${token}`
  );
}

  // Admin Email
  sendAdminEmail(data: { user_ids: string[]; subject: string; message: string; email_type: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/send-email`, data, { headers: this.getAdminHeaders() });
  }


adminLogin(email: string, password: string): Observable<any> {
  return this.http.post<any>(
    `${this.apiUrl}/admin/login`,
    { email, password }
  ).pipe(
    tap((res) => {

      console.log('ADMIN RESPONSE =>', res);

      localStorage.setItem(
        'admin_token',
        res.session_token
      );

    })
  );
}

  adminLogout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/logout`, {}, { headers: this.getAdminHeaders() })
      .pipe(tap(() => localStorage.removeItem('admin_token')));
  }

  getAdminDashboard(): Observable<AdminDashboard> {
    return this.http.get<AdminDashboard>(`${this.apiUrl}/admin/dashboard`, { headers: this.getAdminHeaders() });
  }

  getAdminUsers(page = 1, limit = 15, search?: string, status?: string): Observable<UsersResponse> {
    let url = `${this.apiUrl}/admin/users?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return this.http.get<UsersResponse>(url, { headers: this.getAdminHeaders() });
  }

  getAdminUserDetail(userId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/admin/users/${userId}`, { headers: this.getAdminHeaders() });
  }

  createBillingPortal(returnUrl: string): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.apiUrl}/payments/billing-portal`, { return_url: returnUrl }, { headers: this.getHeaders() });
  }

  updateAdminUser(userId: string, data: { country?: string; notes?: string }): Observable<any> {
    return this.http.put(`${this.apiUrl}/admin/users/${userId}`, data, { headers: this.getAdminHeaders() });
  }
 sendBeneficiaryOtp(identifier: string) {
  return this.http.post(
    `${this.apiUrl}/auth/beneficiary/request-otp`,
    {
      identifier
    }
  );
}
acceptInvite(token: string) {
  return this.http.get(
    `${environment.apiUrl}/beneficiary/accept-invite?token=${token}`,
    { responseType: 'text' as const } 
  );
}

getBeneficiaries() {
  return this.http.get(
    `${this.apiUrl}/beneficiary/beneficiaries`,
    {
      headers: this.getBeneficiaryHeaders()
    }
  );
}

verifyBeneficiaryOtp(identifier: string, otp: string) {
  return this.http.post<any>(
    `${this.apiUrl}/auth/beneficiary/verify-otp`,
    {
      identifier,
      otp
    },
    {
      withCredentials: true
    }
  ).pipe(
    tap((res) => {
      // optional local storage backup
      localStorage.setItem(
        'beneficiary_token',
        res.session_token
      );

      if (res.user) {
        localStorage.setItem(
          'beneficiary_user',
          JSON.stringify(res.user)
        );
      }
    })
  );
}

getBeneficiaryList() {
  return this.http.get(

    `${this.apiUrl}/auth/beneficiary/list`,
    {
      headers: this.getBeneficiaryHeaders(),
      withCredentials: true
    }
  );
}
selectBeneficiary(beneficiaryId: string) {
  return this.http.post<any>(
    `${this.apiUrl}/auth/beneficiary/select`,
    { beneficiaryId },
    {
      headers: this.getBeneficiaryHeaders(),
      withCredentials: true
    }
  ).pipe(
    tap((res) => {

      // save selected beneficiary
      localStorage.setItem('beneficiaryId', beneficiaryId);

      // save session token for quick actions
      if (res.session_token) {
        localStorage.setItem(
          'session_token',
          res.session_token
        );
      }
    })
  );
}

getCriticalContacts(page: number = 1, limit: number = 10, search: string = '') {

  const params: any = {
    page: page,
    limit: limit
  };

  if (search) {
    params.search = search;
  }

  return this.http.get<any>(
    `${this.apiUrl}/contact_directory/contact_directories`,
    {
      headers: this.getHeaders(),
      params: params,
      withCredentials: true
    }
  );
}
getChecklistDetails(
  page: number = 1,
  limit: number = 10,
  search: string = ''
) {

  const params: any = {
    page: page,
    limit: limit
  };

  if (search) {
    params.search = search;
  }

  return this.http.get<any>(
    `${this.apiUrl}/task/task_details`,
    {
      headers: this.getHeaders(),
      params: params,
      withCredentials: true
    }
  );
}
completeTask(taskId: string) {
  return this.http.put(
    `${this.apiUrl}/task/complete/${taskId}`, // ✅ REMOVE extra /api
    {},
    {
      headers: this.getHeaders(),
      withCredentials: true
    }
  );
}
getMemoriesAlbumList() {
  return this.http.get<any[]>(
    `${this.apiUrl}/memories/albumList`,
    {
      headers: this.getHeaders(),
      withCredentials: true
    }
  );
}
getMemories() {
  return this.http.get<any>(
    `${this.apiUrl}/memories/memoriesList?page=1&limit=10`,
    {
      headers: this.getHeaders(),
      withCredentials: true
    }
  );
}
  private getAdminHeaders(): HttpHeaders {
    const token = localStorage.getItem('admin_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    });
  }

  private handleAuthResponse(res: AuthResponse): void {
    localStorage.setItem('session_token', res.session_token);
    localStorage.setItem('user', JSON.stringify(res.user));
    this.currentUserSubject.next(res.user);
  }
  private clearAuth(): void {
  localStorage.removeItem('session_token');
  localStorage.removeItem('user');
  this.currentUserSubject.next(null);

  localStorage.setItem('loginType', 'customer');
}

  

  isLoggedIn(): boolean {
    return !!localStorage.getItem('session_token');
  }

  isAdminLoggedIn(): boolean {
    return !!localStorage.getItem('admin_token');
  }
  private clearBeneficiaryAuth(): void {
  localStorage.removeItem('beneficiary_token');
  localStorage.removeItem('beneficiary_user');
  localStorage.removeItem('beneficiaryId');

  localStorage.setItem('loginType', 'beneficiary');
}

getSubscription() {
  return this.http.get(
    `${this.apiUrl}/subscriptions/active`, { headers: this.getHeaders() }
  );
}
getProgress() {
  return this.http.get<any>(
    `${this.apiUrl}/progress`,
    {
      headers: this.getHeaders(),
      withCredentials: true
    }
  );
}

}